/**
 * Normalized CMA data cell used throughout exports & table rendering.
 * `difference` and `highlight` are optional metadata provided by upstream utils.
 */
export type CMAValueObject = {
  value: number | null;
  difference?: number | null;
  highlight?: boolean;
};

export const isValueObject = (value: any): value is CMAValueObject =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value) && 'value' in value;

export const extractNumericValue = (value: any): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }

  if (Array.isArray(value)) {
    return value.length ? extractNumericValue(value[0]) : null;
  }

  if (isValueObject(value)) {
    return extractNumericValue(value.value);
  }

  return null;
};

export const extractDifferenceValue = (value: any): number | null | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    if (value.length > 1) {
      return extractNumericValue(value[1]);
    }
    return undefined;
  }

  if (isValueObject(value)) {
    if ('difference' in value) {
      return extractNumericValue(value.difference);
    }
    return undefined;
  }

  return undefined;
};

export const toComparisonValueInput = (value: any): number | [number, number] | undefined => {
  const numericValue = extractNumericValue(value);
  if (numericValue === null) {
    return undefined;
  }

  const differenceValue = extractDifferenceValue(value);
  if (differenceValue === undefined || differenceValue === null) {
    return numericValue;
  }

  return [numericValue, differenceValue];
};

export const getSeriesValue = (cell: any, index: number) => {
  if (Array.isArray(cell)) {
    return cell[index];
  }
  return index === 0 ? cell : undefined;
};

export const getComparisonSeriesValue = (cell: any, index: number) =>
  toComparisonValueInput(getSeriesValue(cell, index));

export const getNumericSeriesValue = (cell: any, index: number) => extractNumericValue(getSeriesValue(cell, index));

export const getHighlightFlag = (cell: any, index: number): boolean => {
  const seriesValue = getSeriesValue(cell, index);

  if (Array.isArray(cell)) {
    const value = cell[index];
    if (isValueObject(value)) {
      return Boolean(value.highlight);
    }
  }

  if (isValueObject(cell)) {
    return Boolean(cell.highlight);
  }

  if (isValueObject(seriesValue)) {
    return Boolean(seriesValue.highlight);
  }

  return false;
};
